import React, { useState } from 'react';
import { company } from '../../mock';
import { officeGroups } from '../../lib/offices';
import { Mail, Phone, MapPin, Globe, Facebook, Instagram, Youtube, Navigation } from 'lucide-react';
import { useI18n } from '../../i18n';
import OfficeWorldMap from './OfficeWorldMap';

const allFlat = officeGroups.flatMap((g) => g.offices.map((o) => ({ ...o, country: g.country })));

const Footer = () => {
  const { t } = useI18n();
  const servicesList = t('footer.servicesList') || [];
  const companyList = t('footer.companyList') || [];
  const [activeCountry, setActiveCountry] = useState('All');
  const [activeOffice, setActiveOffice] = useState(allFlat[0]);
  const totalOffices = allFlat.length;
  const visibleOffices = activeCountry === 'All' ? allFlat : allFlat.filter((o) => o.country === activeCountry);
  const pickCountry = (c) => {
    setActiveCountry(c);
    const list = c === 'All' ? allFlat : allFlat.filter((o) => o.country === c);
    setActiveOffice(list[0]);
  };
  return (
    <footer id="contact" className="bg-slate-950 text-slate-300">
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-orange-400">{t('footer.globalKicker')}</span>
              <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold text-white">{t('footer.globalTitle')}</h3>
              <p className="mt-2 text-sm text-slate-400 max-w-xl">{t('footer.globalSub')}</p>
            </div>
            <div className="flex items-center gap-8" data-testid="footer-global-stats">
              <div>
                <div className="text-3xl font-extrabold text-white">{totalOffices}</div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t('footer.officesLabel')}</div>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div>
                <div className="text-3xl font-extrabold text-white">{officeGroups.length}</div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t('footer.countriesLabel')}</div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-2" data-testid="footer-country-filter">
            {['All', ...officeGroups.map((g) => g.country)].map((c) => (
              <button
                key={c}
                data-testid={`footer-country-pill-${c.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => pickCountry(c)}
                className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${
                  activeCountry === c
                    ? 'bg-orange-500 border-orange-500 text-white'
                    : 'border-white/15 text-slate-300 hover:border-orange-400/60 hover:text-white'
                }`}
              >
                {c === 'All' ? t('footer.globalAll') : c}
                <span className={`ml-1.5 ${activeCountry === c ? 'text-orange-100' : 'text-slate-500'}`}>
                  {c === 'All' ? totalOffices : officeGroups.find((g) => g.country === c)?.offices.length}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-2">
              <OfficeWorldMap offices={visibleOffices} active={activeOffice} onSelect={setActiveOffice} />
            </div>
            <div className="space-y-4" data-testid="footer-offices">
              {activeOffice && (
                <div className="rounded-2xl border border-orange-400/30 bg-white/[0.06] p-5" data-testid="footer-active-office">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-white font-bold">
                      <MapPin className="h-4 w-4 text-orange-400 shrink-0" /> {activeOffice.label}
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-orange-300 bg-orange-500/10 border border-orange-400/30 rounded-full px-2 py-0.5">{activeOffice.country}</span>
                  </div>
                  <div className="mt-2 text-sm text-slate-400 leading-relaxed">{activeOffice.address}</div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${activeOffice.lat},${activeOffice.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-active-directions"
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400 hover:text-orange-300"
                  >
                    <Navigation className="h-3.5 w-3.5" /> {t('contactMap.directions')}
                  </a>
                </div>
              )}
              <div className="max-h-56 overflow-y-auto pr-1 space-y-1.5">
                {visibleOffices.map((o) => (
                  <button
                    key={`${o.country}-${o.label}`}
                    onClick={() => setActiveOffice(o)}
                    data-testid={`footer-office-item-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={`w-full text-left flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs transition-colors ${
                      activeOffice && activeOffice.label === o.label
                        ? 'border-orange-400/50 bg-orange-500/10 text-white'
                        : 'border-white/10 text-slate-300 hover:border-orange-400/40 hover:text-white'
                    }`}
                  >
                    <span className="font-semibold truncate">{o.label}</span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider shrink-0">{o.country}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <img src={company.logo} alt="Hipro Technologies" className="h-12 w-auto rounded-md" />
          <div className="mt-3 text-sm font-extrabold text-white tracking-wide" data-testid="footer-brand-tagline">Innovate. Build. Scale<span className="text-orange-400">.</span></div>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t('footer.tagline')}</p>
          <div className="mt-5 flex gap-3">
            {[
              { Icon: Instagram, href: 'https://www.instagram.com/hiprotechnologies/', label: 'Instagram' },
              { Icon: Facebook, href: 'https://www.facebook.com/hiprotechnology', label: 'Facebook' },
              { Icon: Youtube, href: 'https://www.youtube.com/@HiproTechnologies', label: 'YouTube' },
            ].map(({ Icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} data-testid={`footer-social-${label.toLowerCase()}`} className="h-9 w-9 rounded-full border border-white/10 flex items-center justify-center hover:bg-orange-500 hover:border-orange-500 transition-colors">
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold">{t('footer.servicesTitle')}</h4>
          <ul className="mt-4 space-y-2 text-sm">
            {servicesList.map((s) => (<li key={s}><a href="#services" className="hover:text-orange-400">{s}</a></li>))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold">{t('footer.companyTitle')}</h4>
          <ul className="mt-4 space-y-2 text-sm">
            {companyList.map((s, i) => {
              const hrefs = ['/about-us', '/portfolio', '/#industries', '/blog', '/careers', '/#contact'];
              return (<li key={s}><a href={hrefs[i] || '/'} className="hover:text-orange-400">{s}</a></li>);
            })}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold">{t('footer.contactTitle')}</h4>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-orange-400 mt-0.5" /><a href={`mailto:${company.email}`} className="hover:text-orange-400">{company.email}</a></li>
            <li className="flex items-start gap-2"><Phone className="h-4 w-4 text-orange-400 mt-0.5" /><a href={`tel:${company.phoneRaw}`} className="hover:text-orange-400">{company.phone}</a></li>
            <li className="flex items-start gap-2"><Globe className="h-4 w-4 text-orange-400 mt-0.5" /><span>{company.website}</span></li>
            <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-orange-400 mt-0.5" /><span>India · USA · UK · UAE · Malaysia · Netherlands · Hong Kong</span></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <div>© 2015 - {new Date().getFullYear()} {company.name}. {t('footer.rightsReserved')}</div>
          <div className="flex gap-5">
            <a href="/press" data-testid="footer-press-link" className="hover:text-orange-400">Press</a>
            <a href="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-orange-400">{t('footer.privacy')}</a>
            <a href="/terms-of-service" data-testid="footer-terms-link" className="hover:text-orange-400">{t('footer.terms')}</a>
            <a href="/cookie-policy" data-testid="footer-cookies-link" className="hover:text-orange-400">{t('footer.cookies')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
